"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./LFGSlideshow.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiNftMedia } from "@/generated/models/ApiNftMedia";
import { enterArtFullScreen, fullScreenSupported } from "@/helpers/Helpers";
import { createPortal } from "react-dom";
const DEFAULT_TIMEOUT = 10000;
const SLIDESHOW_ID = "lfg-slideshow";
const EMPTY_CAPTIONS_SRC = "data:text/vtt,WEBVTT%0A%0A";
const getMediaField = (value: unknown) => typeof value === "string" ? value : "";
const isVideo = (url: string | null | undefined) => getMediaField(url).toLowerCase().endsWith(".mp4");
const preloadMedia = (mediaItem: ApiNftMedia) => {
  const animation = getMediaField(mediaItem.animation);
  const image = getMediaField(mediaItem.image);
  if (isVideo(animation)) {
    const video = document.createElement("video");
    video.src = animation;
    video.load();
  } else if (image.length > 0) {
    const img = new Image();
    img.src = image;
  }
};
const preloadNext = (mediaItems: ApiNftMedia[], index: number) => {
  const nextIndex = (index + 1) % mediaItems.length;
  const nextMedia = mediaItems[nextIndex];
  if (!nextMedia) {
    return;
  }
  preloadMedia(nextMedia);
};
const LFGSlideshow: React.FC<{
  isOpen: boolean;
  contract: string;
  setIsOpen: (isOpen: boolean) => void;
}> = t0 => {
  const $ = _c(49);
  const {
    isOpen,
    contract,
    setIsOpen
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const slideTimer = useRef(null);
  const videoRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [media, setMedia] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (slideTimer.current !== null) {
        clearTimeout(slideTimer.current);
        slideTimer.current = null;
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSlideTimer = t2;
  let t3;
  if ($[2] !== media.length) {
    t3 = () => {
      clearSlideTimer();
      setCurrentIndex(prevIndex => media.length > 0 ? (prevIndex + 1) % media.length : prevIndex);
    };
    $[2] = media.length;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const nextSlide = t3;
  let t4;
  if ($[4] !== setIsOpen) {
    t4 = () => {
      clearSlideTimer();
      setIsOpen(false);
    };
    $[4] = setIsOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const closeSlideshow = t4;
  let t5;
  let t6;
  if ($[6] !== closeSlideshow || $[7] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          closeSlideshow();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t6 = [closeSlideshow, isOpen];
    $[6] = closeSlideshow;
    $[7] = isOpen;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      const previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousBodyOverflow;
      };
    };
    t8 = [isOpen];
    $[10] = isOpen;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== contract) {
    t9 = () => {
      let canceled = false;
      commonApiFetch({
        endpoint: `nfts/${contract}/media`
      }).then(loadedMedia => {
        if (canceled) {
          return;
        }
        setMedia(loadedMedia);
        setCurrentIndex(0);
        const firstMedia = loadedMedia[0];
        if (firstMedia) {
          preloadMedia(firstMedia);
        }
      }).catch(() => {
        if (!canceled) {
          setMedia([]);
          setCurrentIndex(0);
        }
      });
      return () => {
        canceled = true;
      };
    };
    t10 = [contract];
    $[13] = contract;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== currentIndex || $[17] !== isOpen || $[18] !== media || $[19] !== nextSlide) {
    t11 = () => {
      if (!isOpen || media.length === 0) {
        return;
      }
      const currentMedia = media[currentIndex];
      if (!currentMedia) {
        return;
      }
      preloadNext(media, currentIndex);
      if (isVideo(currentMedia.animation)) {
        const videoElement = videoRef.current;
        if (videoElement !== null) {
          const handleEnded = () => {
            nextSlide();
          };
          const handleVolumeChange = () => {
            setIsMuted(videoElement.muted);
          };
          videoElement.addEventListener("ended", handleEnded);
          videoElement.addEventListener("volumechange", handleVolumeChange);
          return () => {
            videoElement.removeEventListener("ended", handleEnded);
            videoElement.removeEventListener("volumechange", handleVolumeChange);
          };
        }
      } else {
        slideTimer.current = setTimeout(nextSlide, DEFAULT_TIMEOUT);
      }
      return () => {
        clearSlideTimer();
      };
    };
    t12 = [clearSlideTimer, currentIndex, isOpen, media, nextSlide];
    $[16] = currentIndex;
    $[17] = isOpen;
    $[18] = media;
    $[19] = nextSlide;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  const currentMedia_0 = media[currentIndex];
  if (!isOpen || !currentMedia_0) {
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <></>;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    return t13;
  }
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[23] !== closeSlideshow || $[24] !== currentIndex || $[25] !== currentMedia_0.animation || $[26] !== currentMedia_0.image || $[27] !== isMuted) {
    const currentAnimation = getMediaField(currentMedia_0.animation);
    let t18;
    if ($[33] !== currentMedia_0.image) {
      t18 = getMediaField(currentMedia_0.image);
      $[33] = currentMedia_0.image;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    const currentImage = t18;
    t16 = styles.slideshowContainer;
    let t19;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = fullScreenSupported() && <FontAwesomeIcon icon={faExpand} className={styles.slideButton} onClick={_temp} />;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    if ($[36] !== closeSlideshow) {
      t17 = <span className={styles.slideButtons}>{t19}<FontAwesomeIcon icon={faXmarkCircle} className={styles.slideButton} onClick={closeSlideshow} /></span>;
      $[36] = closeSlideshow;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    t13 = styles.slide;
    t14 = SLIDESHOW_ID;
    t15 = isVideo(currentAnimation) ? <video ref={videoRef} autoPlay={true} controls={true} playsInline={true} muted={isMuted} src={currentAnimation} poster={currentImage.length > 0 ? currentImage : undefined}><track kind="captions" src={EMPTY_CAPTIONS_SRC} srcLang="en" label="No captions available" />Your browser does not support the video tag.</video> : <img src={currentImage} alt={`LFG Slide ${currentIndex + 1}`} />;
    $[23] = closeSlideshow;
    $[24] = currentIndex;
    $[25] = currentMedia_0.animation;
    $[26] = currentMedia_0.image;
    $[27] = isMuted;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t13 = $[28];
    t14 = $[29];
    t15 = $[30];
    t16 = $[31];
    t17 = $[32];
  }
  let t18;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t18 = <div className={t13} id={t14}>{t15}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t16 || $[43] !== t17 || $[44] !== t18) {
    t19 = <div className={t16}>{t17}{t18}</div>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const slideshow = t19;
  if (typeof document === "undefined") {
    let t20;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <></>;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    return t20;
  }
  let t20;
  if ($[47] !== slideshow) {
    t20 = createPortal(slideshow, document.body);
    $[47] = slideshow;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
};
export const LFGButton: React.FC<{
  contract: string;
}> = t0 => {
  const $ = _c(6);
  const {
    contract
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] !== contract || $[1] !== isOpen) {
    t1 = <LFGSlideshow contract={contract} isOpen={isOpen} setIsOpen={setIsOpen} />;
    $[0] = contract;
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" onClick={() => setIsOpen(true)} className="hover:tw-text-primary-200 tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-primary-500/60 tw-bg-primary-500/10 tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-leading-5 tw-text-primary-300 tw-shadow-sm tw-transition tw-duration-300 tw-ease-out hover:tw-border-primary-400 hover:tw-bg-primary-500/15 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-500">LFG: Start the Show!</button>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
function _temp() {
  return enterArtFullScreen(SLIDESHOW_ID);
}